import React, { useEffect, useState } from "react";
import { apiClient } from "../../lib/api";

/**
 * Social proof block powered by /api/masterclass/stats.
 * - "XX personas ya han reservado su plaza"
 * - small line with last 3 recent reservations: initials + first name + country
 *
 * If the API fails OR total is 0, renders NOTHING. Never invents numbers.
 *
 * Variants:
 *   - "compact": inline, small, under the CTA (form context)
 *   - "hero": slightly larger, single line for hero (unused right now but ready)
 */

const AVATAR_TONES = ["#C7AA78", "#8E7A55", "#B79668", "#0B0E13", "#6E7580"];
function toneFor(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AVATAR_TONES[h % AVATAR_TONES.length];
}

export default function SocialProofBlock({ variant = "compact" }) {
  const [state, setState] = useState({ total: null, recent: [], failed: false });

  useEffect(() => {
    let mounted = true;
    const load = () => {
      apiClient
        .get("/masterclass/stats")
        .then((r) => {
          if (!mounted) return;
          const total = typeof r.data.total === "number" ? r.data.total : null;
          const recent = Array.isArray(r.data.recent) ? r.data.recent.slice(0, 3) : [];
          setState({ total, recent, failed: total === null });
        })
        .catch(() => {
          if (mounted) setState({ total: null, recent: [], failed: true });
        });
    };
    load();
    const id = setInterval(load, 60000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  // Fail-closed: if the API failed OR there are no reservations yet, render
  // absolutely nothing. Never invent numbers.
  if (state.failed || state.total === null || state.total < 1) return null;

  return (
    <div className="flex flex-col gap-2" data-testid="social-proof-block">
      <p className="text-[13px] text-[#0B0E13] font-sans font-medium leading-tight">
        <span className="font-serif italic text-[#8E7A55] mr-1" data-testid="social-proof-total">
          {state.total}
        </span>
        personas ya están en la lista de espera
      </p>
      {state.recent.length > 0 && (
        <div className="flex items-center gap-3" data-testid="social-proof-recent">
          <div className="flex -space-x-2" aria-hidden="true">
            {state.recent.map((p, i) => (
              <span
                key={i}
                title={p.first_name}
                className="relative inline-flex h-6 w-6 items-center justify-center rounded-full font-sans font-medium text-[9px] tracking-wide"
                style={{
                  background: toneFor(p.initials || p.first_name || String(i)),
                  color: "#F5F1EA",
                  border: "2px solid #FBF7F0",
                  boxShadow: "0 1px 2px rgba(11,14,19,0.10)",
                  zIndex: state.recent.length - i,
                }}
              >
                {p.initials || "·"}
              </span>
            ))}
          </div>
          <p className="text-[11px] text-[#6E7580] font-sans font-light leading-none">
            {state.recent
              .map((p) => {
                const parts = [p.first_name];
                if (p.country) parts.push(p.country);
                return parts.filter(Boolean).join(" · ");
              })
              .join(" · ")}
          </p>
        </div>
      )}
    </div>
  );
}
